'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import HeroPrimary from '@src/components/Shared/HeroPrimary';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import InlineSVG from '@src/components/Shared/InlineSVG';
import NextImage from '@src/components/Shared/NextImage';

import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import HotSpots from '@src/components/Sections/HotSpots';
import ImageTextPrimary from '@src/components/Sections/ImageTextPrimary';
import ServiceOverview from '@src/components/Sections/ServiceOverview';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import { ProductsClientPropTypes } from './ProductsPage.types';
import constructURL from '@src/utils/constructURL';
import Testimonials from '@src/components/Sections/Testimonials';

const ProductsPageClient: React.FC<ProductsClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products`, baseURL: '' }));
  setContentType('site-page');

  return (
    <>
      <HeroPrimary
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#050505]'
        attr='hero-component'
        className='bg-background-primary! mb-[0rem]! flex flex-col items-center justify-between overflow-visible! pt-[6rem] pb-[3rem] md:flex-row md:px-[4rem]! md:pt-[9.3rem] md:pb-[6.3rem] lg:gap-[2rem] lg:pt-[11.2rem] lg:pb-[8.2rem] 2xl:h-[75rem]! 2xl:py-[9.6rem]'
        title={page?.hero?.items?.[0]?.title}
        bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        image={page?.hero?.items?.[0]?.mediaPrimary}
        titleClassName='text-[4rem] lg:text-[6rem] 2xl:tracking-[-0.2432rem] leading-[4.7rem] lg:leading-[6.9rem] md:max-w-[36.6rem] md:max-w-3xl lg:max-w-[50rem] 2xl:max-w-full 2xl:tracking-[-0.152rem]'
        imgClassName='overflow-hidden absolute! sm:relative! top-[2rem] sm:top-[0rem] sm:overflow-visible! flex items-center justify-center md:items-center md:justify-end xl:[&>img]:max-h-[60rem] top-[0] h-full w-full md:w-[50%] opacity-[0.2] sm:opacity-[1] [&>img]:w-auto! [&>img]:max-w-[95%]!'
        bodyCopyClassName='[&>p]:text-[1.6rem] md:[&>p]:text-[1.8rem] lg:[&>p]:text-[2rem] 2xl:[&>p]:text-[2rem] [&>p]:leading-[150%] md:max-w-[34.2rem] md:max-w-[43.7rem] lg:max-w-[51.7rem] 2xl:max-w-[59rem] relative md:top-[0] mt-[1.6rem]'
        childrenClassName='xsm:max-w-full'
      />

      <ImageTextSecondary
        reversed
        priority
        id={page?.measureWhatMatters?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.measureWhatMatters?.items?.[0]?.title}
        imgURL={page?.measureWhatMatters?.items?.[0]?.mediaPrimary?.url}
        sectionHeaderClassName='lg:py-[5rem]!'
        className='overflow-visible! py-[8rem]'
        objectFit='contain'
        background='lg:bg-background-tertiary'
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.measureWhatMatters?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons
              className='[&>div>a[data-pseudo]]:text-shadow-custom'
              items={page?.measureWhatMatters?.items?.[0]?.buttonCollection?.items}
            />
          </>
        }
      />

      <Container
        attr='full-feature-component'
        id={page?.measurementSolutions?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0]! md:flex md:flex-col'
      >
        <SectionHeader
          className='mb-[3.4rem] justify-center lg:pb-[unset]'
          pretitle={page?.measurementSolutions?.items?.[0]?.preTitle}
          title={page?.measurementSolutions?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] xl:leading-[5.4rem] font-light 2xl:max-w-[100rem] '
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.measurementSolutions?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        </SectionHeader>

        <InlineSVG
          className='mx-auto my-[8rem] hidden w-[80%] lg:block'
          src={page?.measurementSolutions?.items?.[0]?.mediaPrimary?.url}
          renderAt={['desktop']}
        />
        <InlineSVG
          className='mx-auto my-[8rem] hidden w-[80%] md:block lg:hidden'
          src={page?.measurementSolutions?.items?.[0]?.mediaSecondary?.url}
          renderAt={['tablet']}
        />
        <InlineSVG
          className='mx-auto my-[8rem] block w-[80%] md:hidden'
          src={page?.measurementSolutions?.items?.[0]?.mediaTertiary?.url}
          renderAt={['mobile']}
        />
      </Container>

      <Container
        id={page?.centralizedPlatform?.items?.[0]?.scrollTo?.slice(1)}
        className='bg-background-primary z-[unset]! pb-[5rem] lg:bg-[unset] lg:pb-[0rem]'
      >
        <ImageTextSecondary
          reversed
          pretitle={page?.centralizedPlatform?.items?.[0]?.preTitle}
          title={page?.centralizedPlatform?.items?.[0]?.title}
          titleClassName='text-[#fff]! text-[4rem] xl:text-[6.4rem] [&>span]:leading-[1.2] mb-[2.5rem]'
          sectionHeaderClassName='pt-[6rem] lg:pt-[12rem]'
          imgURL={page?.centralizedPlatform?.items?.[0]?.mediaPrimary?.url}
          imgSecondClassName='hidden!'
          imgFirstClassName='hidden!'
          textContainerClassName='w-full!'
          className='z-[unset]! mb-[0rem]! overflow-visible! text-[#fff]! lg:pt-[0rem]! lg:pb-[15rem]!'
          objectFit='contain'
          background='bg-background-primary'
          childrenElements={
            <>
              <RenderBodyCopy
                bodyCopy={page?.centralizedPlatform?.items?.[0]?.bodyCopy?.json?.content}
                className='max-w-[62rem]!'
              />
              <RenderButtons
                className='[&>div>a[data-pseudo]]:text-shadow-custom'
                items={page?.centralizedPlatform?.items?.[0]?.buttonCollection?.items}
              />
            </>
          }
        />
        <NextImage
          src={page?.centralizedPlatform?.items?.[0]?.mediaPrimary?.url}
          alt='valdhub dashboard'
          className='lg:relative lg:top-[-30rem] lg:right-[-11rem] lg:mb-[-30rem] 2xl:right-[-9rem]'
        ></NextImage>
      </Container>

      <Container
        attr='full-feature-component'
        id={page?.normsHotSpot?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0]! md:flex md:flex-col'
      >
        <SectionHeader
          className='mb-[3.4rem] justify-center lg:pb-[unset]'
          pretitle={page?.normsHotSpot?.items?.[0]?.preTitle}
          title={page?.normsHotSpot?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5.4rem] font-light 2xl:max-w-[100rem] '
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.normsHotSpot?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.normsHotSpot?.items?.[0]?.buttonCollection?.items} className='justify-center' />
          </>
        </SectionHeader>

        <HotSpots data={page?.normsHotSpot?.items?.[0]} />
      </Container>

      <SectionHeader
        headingLevel='h3'
        className='mb-[7rem] justify-center px-[2.4rem] lg:pb-[unset]'
        pretitle={page?.dataPoints?.items?.[0]?.preTitle}
        title={page?.dataPoints?.items?.[0]?.title}
        titleClassName='text-[2.8rem] xl:text-[2.2rem] [&>span]:text-shadow-custom mb-[2.4rem] leading-[3.2rem] md:leading-[2.6rem] xl:leading-[3.4rem] font-normal 2xl:max-w-[100rem] '
        childrenClassName='max-w-4xl!'
        align='center'
      >
        <>
          <RenderBodyCopy bodyCopy={page?.dataPoints?.items?.[0]?.bodyCopy?.json?.content} />
          <RenderButtons items={page?.dataPoints?.items?.[0]?.buttonCollection?.items} className='justify-center' />
        </>
      </SectionHeader>

      <ImageTextPrimary
        reversed
        id={page?.exerciseTools?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.exerciseTools?.items?.[0]?.preTitle}
        title={page?.exerciseTools?.items?.[0]?.title}
        imgURL={page?.exerciseTools?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.exerciseTools?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.exerciseTools?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <NextImage
        id={page?.athletesSucceed?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.athletesSucceed?.items?.[0]?.mediaPrimary?.url}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[5.6rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={page?.athletesSucceed?.items?.[0]?.title}
            titleClassName='text-left md:text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] 2xl:max-w-[78rem] lg:text-[4.8rem] 2xl:text-[6.2rem] lg:leading-[5rem] 2xl:leading-[6.2rem]'
            childrenClassName='relative max-w-[88.8rem]!'
            align='center'
          >
            <RenderBodyCopy
              className='x-auto text-body-copy--light mt-[2.4rem] max-w-[51.7rem] text-left font-extralight md:text-center md:text-[2.4rem]! 2xl:mt-[3.2rem] 2xl:max-w-[88.8rem]'
              bodyCopy={page?.athletesSucceed?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.athletesSucceed?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </Container>
      </NextImage>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <ServiceOverview
        id={page?.clientSuccess?.items?.[0]?.scrollTo?.slice(1)}
        description={page?.clientSuccess?.items?.[0]?.bodyCopy}
        services={page?.clientSuccess?.items?.[0]?.topicCollection?.items}
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.cta?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.cta?.items?.[0]?.title}
        bodyCopy={page?.cta?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.cta?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default ProductsPageClient;
